import React from 'react'
import { css } from '@emotion/css'
import { FormattedMessage, useIntl } from 'react-intl'
import { Props as IntlProps } from 'react-intl/src/components/message'
import { TextInput } from '../form-text'
import { theme as deprecatedTheme } from '../../styled'
import {
  IconAdd,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { ContrastFormattedMessage } from '../../components/contrast-formatted-message'
import { Dropdown } from '../dropdown'
import { Button } from '@contrast-security-inc/design-system-buttons'

export function ControlsWithData({
  onSearch,
  searchPlaceholder,
  description,
  onClickAdd,
  addBtnTitle,
  addBtnDataE2e
}: {
  readonly onSearch: (string: any) => void
  readonly searchPlaceholder: IntlProps
  readonly description?: IntlProps
  readonly onClickAdd?: () => void
  readonly addBtnTitle?: IntlProps
  readonly addBtnDataE2e?: string
}) {
  return (
    <React.Fragment>
      {!!description && <Description content={description} />}
      <div
        className={css`
          display: flex;
          padding: 10px 11px 9px 13px;
        `}
      >
        <span
          className={css`
            flex: 2;
          `}
        >
          <ContrastFormattedMessage {...searchPlaceholder}>
            {(placeholderMessage) => (
              <TextInput
                label=""
                renderLabel={() => false}
                placeholder={placeholderMessage}
                onChange={onSearch}
              />
            )}
          </ContrastFormattedMessage>
        </span>
        <span
          className={css`
            flex: 3;
            display: flex;
            justify-content: flex-end;
          `}
        >
          {!!onClickAdd && addBtnTitle && (
            <AddButton
              onClick={onClickAdd}
              title={addBtnTitle}
              dataE2e={addBtnDataE2e}
            />
          )}
        </span>
      </div>
    </React.Fragment>
  )
}

export function ControlsNoData({
  description,
  onClickAdd,
  addBtnTitle,
  addBtnDataE2e
}: {
  readonly description?: IntlProps
  readonly addBtnTitle?: IntlProps
  readonly addBtnDataE2e?: string
  readonly onClickAdd?: () => void
}) {
  return (
    <React.Fragment>
      <div
        className={css`
          display: flex;
          padding: 10px 11px 9px 13px;
        `}
      >
        {!!description && <Description content={description} />}
        {!!onClickAdd && addBtnTitle && (
          <span
            className={css`
              flex: 2;
              display: flex;
              justify-content: flex-end;
            `}
          >
            <AddButton
              onClick={onClickAdd}
              title={addBtnTitle}
              dataE2e={addBtnDataE2e}
            />
          </span>
        )}
      </div>
    </React.Fragment>
  )
}

export interface LegacyQuickFilter {
  readonly name: string
  readonly count: number
  readonly filterType: string
}

export const quickFiltersDropdownTestId = 'legacy-grid-quick-filters-dropdown'

export function QuickFiltersControls({
  placeholderId = 'placeholder_find',
  onSearch,
  onFilterChange,
  quickFilters,
  selectedQuickFilter
}: {
  readonly placeholderId: string
  readonly onSearch: (term: string) => void
  readonly onFilterChange: (filter: LegacyQuickFilter) => void
  readonly quickFilters: LegacyQuickFilter[]
  readonly selectedQuickFilter: string
}) {
  const { formatMessage } = useIntl()
  return (
    <div
      className={css`
        display: flex;
        padding: 10px 11px 9px 13px;
      `}
    >
      <div
        className={css`
          display: inline-flex;
          flex-basis: 33%;
        `}
      >
        <Dropdown<LegacyQuickFilter>
          menuE2eTestId="legacy-grid-quick-filters-menu"
          styleOverrides={{
            outerContainerCss: css`
              width: unset;
            `,
            menuCss: css`
              width: unset;
            `
          }}
          selectedItem={quickFilters.find(
            (qf) => qf.filterType === selectedQuickFilter
          )}
          items={quickFilters}
          selectionItemRenderer={({ item }) => (
            <>
              <span
                style={{
                  fontWeight:
                    deprecatedTheme.fontWeights.primaryCondensed.regular,
                  fontFamily: deprecatedTheme.fontFamilies.primaryCondensed
                }}
              >
                {item.name}
              </span>
              <span
                style={{
                  color: theme.color.neutral50,
                  fontFamily: deprecatedTheme.fontFamilies.primaryCondensed
                }}
              >{` (${item.count})`}</span>
            </>
          )}
          menuItemRenderer={({ item, isSelected }) => (
            <>
              <span
                style={{
                  fontWeight: isSelected
                    ? deprecatedTheme.fontWeights.primaryCondensed.bold
                    : deprecatedTheme.fontWeights.primaryCondensed.regular,
                  fontFamily: deprecatedTheme.fontFamilies.primaryCondensed
                }}
              >
                {item.name}
              </span>
              <span
                style={{
                  color: theme.color.neutral50,
                  fontFamily: deprecatedTheme.fontFamilies.primaryCondensed
                }}
              >{` (${item.count})`}</span>
            </>
          )}
          itemToString={(quickFilter) =>
            `${quickFilter.name} (${quickFilter.count})`
          }
          itemToKey={(quickFilter) => quickFilter.name}
          onChange={onFilterChange}
          unitTestId={quickFiltersDropdownTestId}
        />
        <TextInput
          onChange={onSearch}
          classNameFormTextPrimaryContainer={css`
            margin-left: -2px;
          `}
          placeholder={formatMessage({ id: placeholderId })}
        />
      </div>
    </div>
  )
}

function AddButton({
  onClick,
  title,
  dataE2e
}: {
  readonly onClick: () => void
  readonly title: IntlProps
  readonly dataE2e?: string
}) {
  return (
    <Button
      Icon={IconAdd}
      data-e2e={dataE2e}
      onClick={() => onClick()}
      className={css`
        display: flex;
        align-items: center;
      `}
    >
      <FormattedMessage {...title} />
    </Button>
  )
}

export function Description({ content }: { readonly content: IntlProps }) {
  return (
    <FormattedMessage
      {...content}
      children={(text) => (
        <div
          data-testid="legacy-grid-ip-list-description"
          className={css`
            padding-top: 15px;
            padding-bottom: 10px;
            padding-right: 11px;
            padding-left: 13px;
            color: ${theme.color.neutral60};
          `}
        >
          {text}
        </div>
      )}
    />
  )
}
